import {
  createContext,
  type Dispatch,
  type FC,
  type MutableRefObject,
  type PropsWithChildren,
  type SetStateAction,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';

import { useTapQuarryMutation } from '@/api/queries/quarry';
import { ToastNotification } from '@/common/components/ToastNotification/ToastNotification';
import { useAppSelector } from '@/redux/store';

interface MineResourceContextType {
  token?: string;
  setToken: Dispatch<SetStateAction<string | undefined>>;
  tapRef: MutableRefObject<Record<string, number> | undefined>;
  setTappingIsStarted: Dispatch<SetStateAction<boolean>>;
}

const MineResourceContext = createContext({} as MineResourceContextType);

const MineResourceProvider: FC<PropsWithChildren> = ({ children }) => {
  const { t } = useTranslation();
  const tapRef = useRef<Record<string, number>>();
  const tapObject = useAppSelector((state) => state.quarry.tap);
  const userSettings = useAppSelector((state) => state.user.user?.settings);
  const [token, setToken] = useState<string | undefined>();
  const [tappingIsStarted, setTappingIsStarted] = useState(false);
  const clearTokenTimerId = useRef<NodeJS.Timeout | undefined>();
  const [tapQuarry] = useTapQuarryMutation({
    fixedCacheKey: 'shared-tap-quarry',
  });
  const [requestId, setRequestId] = useState<string | null>(null);

  //update ref object with tap quantity for each quarry
  useEffect(() => {
    tapRef.current = tapObject;
  }, [tapObject]);

  //check tap quantity and send tap request after token changed
  useEffect(() => {
    if (!tappingIsStarted) {
      return;
    }
    const delay = userSettings?.boostTokenLiveTime
      ? userSettings?.boostTokenLiveTime - 2
      : 8;

    clearTokenTimerId.current = setTimeout(() => {
      if (
        tapRef.current &&
        Object.values(tapRef.current).some((val) => !!val)
      ) {
        for (const quarryId in tapRef.current) {
          const boosts = tapRef.current[quarryId];

          if (boosts > 0) {
            tapQuarry({ quarryId, boosts })
              .unwrap()
              .then((response) => {
                setToken(response.token);
                setRequestId(response.requestId);
              })
              .catch(() => {
                ToastNotification(t('Something went wrong, please try again'));
                setToken(undefined);
              });
          } else {
            setTappingIsStarted(false);
            setRequestId(null);
          }
        }
      } else {
        setTappingIsStarted(false);
        setRequestId(null);
        setToken(undefined);
      }
    }, delay * 1000);

    return (): void => {
      clearTimeout(clearTokenTimerId.current);
    };
  }, [userSettings?.boostTokenLiveTime, tappingIsStarted, requestId]);

  return (
    <MineResourceContext.Provider
      value={{ token, setToken, tapRef, setTappingIsStarted }}
    >
      {children}
    </MineResourceContext.Provider>
  );
};

export { MineResourceContext, MineResourceProvider };
